import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { call, type ListAnswer, type ListCard, type Slab } from '../api';
import { Back, BottomBar, Failed, Spinner, Thumb, Top, useCall, useMe, useSignedIn } from '../components/parts';
import { bumpList, mergePhoneList, movingCount, onListChange, readList, writeList, type PhoneCard } from '../phone-list';
import { Link } from '../router';
import { cards, holdOnList, total } from '../words';
import { CancelHold } from './card';

// My list (José, 22 and 23 Oct 2026): the cards a buyer added, one box per shop, with their total. Signed out it is
// this phone's; signed in it is the account's, with the buyer's hold requests on top.

export function ListPage() {
  const signedIn = useSignedIn();
  const moving = useSyncExternalStore(onListChange, movingCount, () => 0);
  return (
    <>
      <Top right={signedIn ? <Link className="mylist" href="/account">Account</Link> : undefined} />
      <main>
        <Back href="/shows" label="All shows" />
        <h1>My list</h1>
        {moving ? (
          <p className="note-box" role="status">
            {cards(moving)} from your old list will come in when this phone has signal.
          </p>
        ) : null}
        {signedIn ? <AccountList /> : <PhoneList />}
      </main>
    </>
  );
}

const EMPTY = 'Your list is empty. Point your phone camera at a GrailTab price label, then tap Add to my list.';

type Tile = { id: string; name: string; grade: string | null; price: string | null; cents: number | null; symbol: string; picture: string | null; slab: Slab | null; shop: string; sold?: boolean };

function PhoneList() {
  const list = useSyncExternalStore(onListChange, () => JSON.stringify(readList()), () => '[]');
  const items = JSON.parse(list) as PhoneCard[];
  const [removed, setRemoved] = useState<{ item: PhoneCard; at: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const remove = (id: string) => {
    const all = readList();
    const at = all.findIndex((x) => x.id === id);
    if (at < 0) return;
    writeList(all.filter((x) => x.id !== id));
    setRemoved({ item: all[at]!, at });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setRemoved(null), 8000);
  };
  const undo = () => {
    if (!removed) return;
    const all = readList();
    all.splice(Math.max(0, Math.min(removed.at, all.length)), 0, removed.item);
    writeList(all);
    setRemoved(null);
  };

  const tiles: Tile[] = items.map((x) => ({
    ...x,
    slab: x.grader && x.grade_value ? { grader: x.grader, grade: x.grade_value } : null,
    shop: [x.shop || 'A GrailTab shop', x.show].filter(Boolean).join('\n'),
  }));

  return (
    <>
      <p className="signin">
        Keep your list on any phone. <Link href="/account?back=%2Flist">Sign in</Link>
      </p>
      <Groups tiles={tiles} onRemove={remove} />
      {removed ? (
        <BottomBar>
          <div className="undo" role="status">
            <span>Card removed.</span>
            <button type="button" onClick={undo}>
              Undo
            </button>
          </div>
        </BottomBar>
      ) : null}
    </>
  );
}

function AccountList() {
  const me = useMe();
  const { data, error, reload } = useCall<ListAnswer>('/api/find/list');
  const [note, setNote] = useState<string | null>(null);

  // Cards still on the phone, from a sign-in that had no signal to move them, go into the account now. Cards that do
  // not fit on a full list stay on the phone, and the page says how many (redteam #251 L1).
  useEffect(() => {
    if (!readList().length) return;
    void mergePhoneList().then((full) => {
      if (full === null) return;
      if (full) setNote(`${full === 1 ? '1 card' : `${full} cards`} from this phone did not fit: your list is full. Remove some, and they move in when you open My list again.`);
      reload();
    });
  }, [reload]);

  if (error) return <Failed error={error} retry={reload} />;
  if (!data) return <Spinner />;

  const remove = async (id: string) => {
    try {
      await call(`/api/find/list/${id}`, { method: 'DELETE' });
      setNote('Card removed.');
      bumpList();
    } catch {
      setNote('That did not work. Try again.');
    }
    reload();
  };

  const tiles: Tile[] = data.cards.map((c: ListCard) => ({ ...c, sold: !c.for_sale }));

  return (
    <>
      {data.holds.length ? (
        <>
          <h2>Hold requests</h2>
          <div className="grp">
            {data.holds.map((h) => (
              <div className="hrow" key={h.id}>
                <Link href={`/i/${h.card.id}`}>
                  <b>{h.card.name}</b>
                  <span>{h.card.shop}</span>
                </Link>
                <span className={h.open ? 'hst on' : 'hst'}>{holdOnList(h)}</span>
                {h.open ? <CancelHold hold={h} label="Cancel" done={reload} className="rm" /> : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
      <Groups tiles={tiles} onRemove={remove} />
      {me ? (
        <p className="lede">
          Signed in as <b>{me.user.email}</b>. <Link href="/account">Your account</Link>
        </p>
      ) : null}
      {note ? (
        <p className="note-box" role="status">
          {note}
        </p>
      ) : null}
    </>
  );
}

/** One box per shop, in the order the buyer added them, each with its cards and total; the whole list's sum on top. */
function Groups({ tiles, onRemove }: { tiles: Tile[]; onRemove: (id: string) => void }) {
  if (tiles.length === 0) return <p className="empty">{EMPTY}</p>;
  const keys: string[] = [];
  const by = new Map<string, Tile[]>();
  for (const t of tiles) {
    if (!by.has(t.shop)) {
      by.set(t.shop, []);
      keys.push(t.shop);
    }
    by.get(t.shop)!.push(t);
  }
  const forSale = (xs: Tile[]) => xs.filter((x) => !x.sold);
  const all = total(forSale(tiles));
  return (
    <>
      <p className="sum">
        {cards(tiles.length)} at {keys.length} {keys.length === 1 ? 'shop' : 'shops'}
        {all ? ` · ${all}` : ''}
      </p>
      {keys.map((k) => {
        const xs = by.get(k)!;
        const [shop, show] = k.split('\n');
        const t = total(forSale(xs));
        return (
          <section className="grp" key={k}>
            <div className="grp-h">
              <b>{shop}</b>
              <span>
                {cards(xs.length)}
                {t ? ` · ${t}` : ''}
              </span>
              {show ? <span>{show}</span> : null}
            </div>
            <div className="row">
              {xs.map((x) => (
                <div className="pocket" key={x.id}>
                  <Thumb id={x.id} name={x.name} picture={x.picture} slab={x.slab} />
                  <span className="nm">{x.name}</span>
                  {x.grade && !x.slab ? <span className="gd">{x.grade}</span> : null}
                  {x.sold ? <span className="sold">Sold</span> : x.price ? <span className="pr">{x.price}</span> : <span className="pr ask">Ask the seller</span>}
                  <button className="rm" type="button" aria-label={`Remove ${x.name}`} onClick={() => onRemove(x.id)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}
